/**
 * AdminBingoGamesTab - Manage active bingo games
 * Allows admins to view and end bingo games
 */
import React, { useState, useEffect } from 'react';
import { 
  Trash2, Loader2, RefreshCw, Disc3, Users, Clock, AlertCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, 
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, 
  AlertDialogTitle, AlertDialogTrigger 
} from '../ui/alert-dialog';
import { toast } from 'sonner';

import { API_URL } from '../../utils/api';

export function AdminBingoGamesTab() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    fetchGames();
  }, []);

  const fetchGames = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/games`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Handle both {games: [...]} and direct array responses
        setGames(data.games || data || []);
      } else {
        toast.error('Failed to load bingo games');
      }
    } catch (error) {
      console.error('Error fetching bingo games:', error);
      toast.error('Error loading bingo games');
    } finally {
      setLoading(false);
    }
  };

  const handleEndGame = async (gameCode) => {
    setDeleting(gameCode);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/games/${gameCode}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Game ended successfully');
        fetchGames();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to end game');
      }
    } catch (error) {
      toast.error('Error ending game');
    } finally {
      setDeleting(null);
    }
  };

  const handleCleanupOldGames = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/games/cleanup?older_than_days=7`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Old games cleaned up');
        fetchGames();
      } else {
        toast.error('Failed to cleanup games');
      }
    } catch (error) {
      toast.error('Error cleaning up games');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  const activeGames = games.filter(g => g.status === 'active');
  const endedGames = games.filter(g => g.status !== 'active');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Bingo Games</h3>
          <p className="text-sm text-slate-500">Manage active and past bingo games</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchGames}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="text-orange-600 border-orange-300 hover:bg-orange-50">
                <Trash2 className="w-4 h-4 mr-2" />
                Cleanup Old Games
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Cleanup Old Games</AlertDialogTitle>
                <AlertDialogDescription>
                  This will delete all bingo games older than 7 days. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleCleanupOldGames} className="bg-orange-600 hover:bg-orange-700">
                  Cleanup
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Active Games */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-green-50">
          <h4 className="font-semibold text-green-800 flex items-center gap-2">
            <Disc3 className="w-5 h-5" />
            Active Games ({activeGames.length})
          </h4>
        </div>
        {activeGames.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Disc3 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            No active bingo games
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {activeGames.map(game => (
              <div key={game.game_code} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="font-mono font-bold text-lg text-violet-600">{game.game_code}</span>
                    <Badge className="bg-green-100 text-green-700">Active</Badge>
                    {game.game_type === 'uk90' && (
                      <Badge variant="outline">UK 90-Ball</Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 mt-1">{game.host_name || 'Unnamed Game'}</p>
                  <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Users className="w-3.5 h-3.5" />
                      {game.player_count || 0} players
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      Started: {formatDate(game.created_at)}
                    </span>
                    {game.club_name && (
                      <span className="text-violet-600">{game.club_name}</span>
                    )}
                  </div>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="text-red-600 border-red-300 hover:bg-red-50"
                      disabled={deleting === game.game_code}
                    >
                      {deleting === game.game_code ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Trash2 className="w-4 h-4 mr-2" />
                      )}
                      End Game
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>End Bingo Game</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to end game <strong>{game.game_code}</strong>? 
                        This will remove the game and all player data.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={() => handleEndGame(game.game_code)}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        End Game
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Past Games */}
      {endedGames.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
            <h4 className="font-semibold text-slate-700 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Past Games ({endedGames.length})
            </h4>
          </div>
          <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
            {endedGames.slice(0, 20).map(game => (
              <div key={game.game_code} className="px-6 py-3 flex items-center justify-between text-sm">
                <div>
                  <span className="font-mono text-slate-600">{game.game_code}</span>
                  <span className="text-slate-400 mx-2">•</span>
                  <span className="text-slate-500">{game.host_name || 'Unnamed'}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-400">{formatDate(game.created_at)}</span>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Game</AlertDialogTitle>
                        <AlertDialogDescription>
                          Delete game {game.game_code}? This cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => handleEndGame(game.game_code)}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {games.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          <AlertCircle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p>No bingo games found</p>
        </div>
      )}
    </div>
  );
}
